/**
 * plan — turn a widget's `wire` block into something callable, or into a REFUSAL that says why.
 *
 * Pure. No network, no clock, no globals. That is deliberate: the hard part of this layer is
 * deciding whether a call is even possible, and that decision should be testable without a server.
 *
 * ── THE MEASUREMENT THAT SHAPED THIS FILE (2026-08-08) ────────────────────────────────────────
 * Across 1,377 widgets in the fleet's codex.json files:
 *
 *     rest         846      of which 818 carry "route": null
 *     graphql      490      of which 15 have no route and 13 no query
 *     data-model    41      not callable by design — a Prisma model, not an endpoint
 *
 * So roughly 503 of 1,377 widgets are actually callable. The rest are DECLARED but not WIRED, and
 * `route: null` is explicit — the key is present on all 846, deliberately null. widget-forge knew it
 * had no route and said so rather than inventing one, which is the compiler behaving correctly.
 *
 * ── WHY THAT IS NOT AN ERROR ─────────────────────────────────────────────────────────────────
 * A client that throws on these would turn two-thirds of the fleet into exceptions, and an exception
 * is a thing a UI shows as a crash. But "this capability is declared and not yet reachable" is a
 * FACT about the system, and the vocabulary for it already exists: FP-018's refusal, carrying a
 * reason. So an unwired widget resolves to `refused('...')` — the same envelope a service returns
 * when it declines to answer, rendered by the same chip, needing no new error type anywhere.
 *
 * The result: this layer cannot fail silently, and it cannot fail loudly-but-uselessly either. It
 * says which of the five states you are in, always.
 */

import { refused } from '@ankr/provenance';

/** Wire kinds this layer understands. A sixth would have to be taught here to exist at all. */
export const WIRE_KINDS = Object.freeze(['graphql', 'rest', 'data-model']);

/** Path params in a REST route, e.g. /api/v2/verdict/:runId → ['runId'] */
export function pathParams(route) {
  return [...String(route || '').matchAll(/:([A-Za-z_][A-Za-z0-9_]*)/g)].map((m) => m[1]);
}

/**
 * Resolve a widget to a callable plan.
 *
 * @returns {{callable:true, kind, method, url?, body?, field?}} or a `refused` envelope.
 */
export function plan(widget, vars = {}, opts = {}) {
  const { baseUrl = '' } = opts;
  const w = widget || {};
  const wire = w.wire || {};
  const slug = w.slug || wire.field || '(unnamed widget)';

  if (!wire.kind)
    return refused(`${slug}: the widget declares no wire.kind, so there is no transport to choose`);

  if (!WIRE_KINDS.includes(wire.kind))
    // NOT a fallback to REST. Guessing a transport is how a client silently calls the wrong thing.
    return refused(`${slug}: unknown wire.kind "${wire.kind}" — this data layer speaks ` +
                   `${WIRE_KINDS.join(', ')}. A new kind is a compiler change, not a guess here.`);

  // ── data-model: 41 in the fleet. A Prisma model is a shape, not an endpoint. ──────────────────
  if (wire.kind === 'data-model')
    return refused(`${slug}: this is a data-model widget (Prisma model ` +
                   `${wire.model || 'unknown'}) — it describes a shape, not a callable endpoint`);

  // widget-forge sets this itself when it could not confirm the endpoint exists. Believe it.
  if (wire.verified_callable === false)
    return refused(`${slug}: widget-forge marked this verified_callable:false — it was generated ` +
                   `from a declaration, and no endpoint was confirmed behind it`);

  if (!wire.route)
    return refused(`${slug}: declared but not wired — wire.route is null. The capability exists in ` +
                   `the codex; the endpoint does not yet. 818 of 846 REST widgets are in this state.`);

  if (wire.kind === 'graphql') {
    if (!wire.query)
      return refused(`${slug}: graphql widget with no query — the field is named but no document ` +
                     `was emitted for it`);
    return {
      callable: true, kind: 'graphql', slug,
      method: 'POST',
      url: `${baseUrl}/graphql`,
      field: wire.field || wire.route,
      body: { query: wire.query, variables: vars },
    };
  }

  // ── rest ─────────────────────────────────────────────────────────────────────────────────────
  const needed = pathParams(wire.route);
  const missing = needed.filter((p) => vars[p] === undefined || vars[p] === null || vars[p] === '');
  if (missing.length)
    // Substituting an empty string would call a DIFFERENT, possibly valid URL. /verdict/ is not
    // /verdict/:runId with runId missing — it may be a list endpoint, and answering from the wrong
    // one is worse than not answering.
    return refused(`${slug}: cannot build the URL — ${missing.length === 1 ? 'path parameter' :
                   'path parameters'} ${missing.map((m) => `:${m}`).join(', ')} not supplied`);

  let path = wire.route;
  for (const p of needed) path = path.replace(`:${p}`, encodeURIComponent(String(vars[p])));

  const method = (wire.method || 'GET').toUpperCase();
  const used = new Set(needed);
  const rest = Object.fromEntries(Object.entries(vars).filter(([k]) => !used.has(k)));
  const qs = method === 'GET' && Object.keys(rest).length
    ? `?${new URLSearchParams(Object.entries(rest).map(([k, v]) => [k, String(v)]))}` : '';

  return {
    callable: true, kind: 'rest', slug, method,
    url: `${baseUrl}${path}${qs}`,
    field: wire.field,
    ...(method !== 'GET' && Object.keys(rest).length ? { body: rest } : {}),
  };
}

/** True when plan() produced something callable rather than a refusal. */
export const isCallable = (p) => Boolean(p && p.callable === true);

/**
 * Survey a registry without calling anything — what a fleet dashboard should show instead of
 * pretending every declared widget works.
 */
export function surveyRegistry(widgets = [], opts = {}) {
  const out = { total: 0, callable: 0, refused: 0, byReason: {}, byKind: {} };
  for (const w of widgets) {
    out.total++;
    const k = w?.wire?.kind || '(none)';
    out.byKind[k] = (out.byKind[k] || 0) + 1;
    const p = plan(w, {}, opts);
    if (isCallable(p)) { out.callable++; continue; }
    out.refused++;
    // Group by the shape of the reason, not the whole string — slugs make every reason unique.
    const key = String(p.reason).replace(/^[^:]+: /, '').split(' —')[0].slice(0, 60);
    out.byReason[key] = (out.byReason[key] || 0) + 1;
  }
  return out;
}
